import { z } from 'zod';
import { pageOf } from '#src/common/pagination.contract.js';
/** Minuto, hora, dia do mês, mês e dia da semana. Sem o campo de segundos. */
const CRON_FIELDS = 5;
function isTimeZone(value) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
/** Instante em ISO 8601, UTC (`2026-10-16T12:00:00.000Z`). */
const instantSchema = z.iso.datetime();
/**
 * Expressão cron de 5 campos. Aqui só se confere o formato; a validação
 * completa (valores e intervalos) é do backend, que usa a mesma biblioteca do
 * agendador.
 */
export const cronExpressionSchema = z
    .string()
    .trim()
    .refine((value) => value.split(/\s+/).length === CRON_FIELDS, 'use 5 campos: minuto hora dia-do-mês mês dia-da-semana');
export const timeZoneSchema = z
    .string()
    .refine(isTimeZone, 'deve ser um fuso IANA, como America/Sao_Paulo');
/** O que a administração define para uma rotina. */
export const routineScheduleInputSchema = z.strictObject({
    /** Desligada, a rotina não roda sozinha, mas ainda pode ser disparada. */
    enabled: z.boolean(),
    cron: cronExpressionSchema,
    timeZone: timeZoneSchema,
});
/** Quem fez uma alteração ou pediu uma execução. */
export const actorSchema = z.strictObject({
    name: z.string(),
    email: z.string(),
});
export const routineScheduleSchema = z.strictObject({
    enabled: z.boolean(),
    cron: z.string(),
    timeZone: z.string(),
    updatedAt: instantSchema,
    /** `null` quando quem alterou não existe mais. */
    updatedBy: actorSchema.nullable(),
});
/**
 * - `schedule`: disparada pelo agendamento.
 * - `manual`: pedida por alguém na administração ou pela API.
 * - `cli`: pela linha de comando (`backend:cli routines:run`).
 */
export const routineTriggerSchema = z.enum(['schedule', 'manual', 'cli']);
/**
 * `abandoned`: o processo que a executava parou no meio (deploy, falha) e
 * outra execução a encerrou. Pode ter enviado parte das mensagens.
 */
export const routineRunStatusSchema = z.enum([
    'running',
    'succeeded',
    'failed',
    'abandoned',
]);
export const routineRunSchema = z.strictObject({
    id: z.uuid(),
    routine: z.string(),
    trigger: routineTriggerSchema,
    status: routineRunStatusSchema,
    /** Horário do agendamento que a disparou; `null` fora do agendamento. */
    scheduledFor: instantSchema.nullable(),
    requestedBy: actorSchema.nullable(),
    startedAt: instantSchema,
    finishedAt: instantSchema.nullable(),
    /** Contadores da execução; as chaves estão em `summaryFields` da rotina. */
    summary: z.record(z.string(), z.number()).nullable(),
    /** Motivo da falha, quando houve. */
    error: z.string().nullable(),
});
export const routineRunsPageSchema = pageOf(routineRunSchema);
/** Rótulo de um contador do resumo, na ordem em que deve aparecer. */
export const routineSummaryFieldSchema = z.strictObject({
    key: z.string(),
    label: z.string(),
});
export const routineSchema = z.strictObject({
    /** Identificador estável, usado nas rotas e no log. */
    name: z.string(),
    title: z.string(),
    description: z.string(),
    summaryFields: z.array(routineSummaryFieldSchema),
    /** `null` enquanto ninguém configurou a rotina: ela não roda sozinha. */
    schedule: routineScheduleSchema.nullable(),
    /** Próximos disparos; vazio quando a rotina está desligada. */
    nextRuns: z.array(instantSchema),
    lastRun: routineRunSchema.nullable(),
    /** Execução em andamento, em qualquer réplica. */
    activeRun: routineRunSchema.nullable(),
});
export const routinesResponseSchema = z.strictObject({
    items: z.array(routineSchema),
});
/** `GET /api/routines/schedule-preview`: confere uma expressão antes de salvar. */
export const schedulePreviewQuerySchema = z.strictObject({
    cron: cronExpressionSchema,
    timeZone: timeZoneSchema,
});
export const schedulePreviewSchema = z.strictObject({
    nextRuns: z.array(instantSchema),
});
//# sourceMappingURL=routines.contract.js.map